import { z } from 'zod'
import { ModelAliasSchema } from './enums.ts'

/**
 * RuntimePolicy profiles — the policy plane's first-class object (PRD-18 AGM-100…106).
 *
 * A policy is COMPLETE, not layered: every field is required, because "which profile does
 * this agent run under" must have one readable answer rather than a merge to trace. The
 * builtin `standard` profile is seeded from environment values, so a fresh install behaves
 * exactly as it did when these numbers lived in env — the env vars become bootstrap
 * defaults and stop being the operating record.
 */

export const RuntimePolicyBodySchema = z
  .object({
    modelAlias: ModelAliasSchema,
    retrieval: z.object({
      strategy: z.enum(['hybrid', 'semantic', 'lexical']),
      topK: z.number().int().min(1).max(50),
      minScore: z.number().min(0),
      candidates: z.number().int().min(10).max(200)
    }),
    budget: z.object({
      maxSteps: z.number().int().positive(),
      maxModelCalls: z.number().int().positive(),
      maxToolCalls: z.number().int().positive(),
      maxDelegations: z.number().int().nonnegative(),
      maxCostUsd: z.number().nonnegative(),
      maxWallClockMs: z.number().int().positive()
    }),
    approval: z.object({
      ttlMs: z.number().int().positive(),
      maxRounds: z.number().int().min(1).max(20)
    }),
    delegation: z.object({
      maxDepth: z.number().int().min(0).max(3),
      maxParallel: z.number().int().min(1).max(8)
    })
  })
  .strict()
export type RuntimePolicyBody = z.infer<typeof RuntimePolicyBodySchema>

export const RuntimePolicySchema = z.object({
  id: z.uuid(),
  name: z.string(),
  description: z.string(),
  body: RuntimePolicyBodySchema,
  /** The seeded `standard` row: undeletable, always assignable, never renamed. */
  builtin: z.boolean(),
  version: z.number().int(),
  /** How many agents currently resolve through it — the blast radius of an edit. */
  assignedAgents: z.number().int().nonnegative(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime()
})
export type RuntimePolicy = z.infer<typeof RuntimePolicySchema>

export const UpsertRuntimePolicyRequestSchema = z
  .object({
    name: z.string().min(1).max(80),
    description: z.string().max(500).default(''),
    body: RuntimePolicyBodySchema,
    note: z.string().max(500).default('')
  })
  .strict()

export const AssignPolicyRequestSchema = z.object({ policyId: z.uuid() }).strict()

export const PolicyAssignmentSchema = z.object({
  agentId: z.string(),
  displayName: z.string(),
  policyId: z.uuid(),
  policyName: z.string()
})
export type PolicyAssignment = z.infer<typeof PolicyAssignmentSchema>

/** Which agents an edit would touch and what changes for each — zero writes (AGM-106). */
export const PolicyDryRunSchema = z.object({
  policyId: z.uuid(),
  agents: z.array(
    z.object({
      agentId: z.string(),
      displayName: z.string(),
      changes: z.array(z.object({ field: z.string(), before: z.unknown(), after: z.unknown() }))
    })
  )
})
export type PolicyDryRun = z.infer<typeof PolicyDryRunSchema>

/** Stamped into every resolved agent and run snapshot: which policy, at which version. */
export const ResolvedPolicyRefSchema = z.object({
  name: z.string(),
  version: z.number().int()
})
export type ResolvedPolicyRef = z.infer<typeof ResolvedPolicyRefSchema>
